import type { SelectedElement } from '../core/state';
import { compressScreenshot } from '../utils/truncate';

const MAX_CAPTURE_DIMENSION = 2560;
const MAX_CAPTURE_PIXELS = 5_000_000;
const MAX_CROP_DIMENSION = 2048;
const MAX_CROP_PIXELS = 2_000_000;

type ScreenFrame = {
  canvas: HTMLCanvasElement;
  scaleX: number;
  scaleY: number;
};

function getBoundedSize(
  width: number,
  height: number,
  maxDimension: number,
  maxPixels: number,
): { width: number; height: number } {
  if (width <= 0 || height <= 0) return { width: 1, height: 1 };

  let boundedWidth = width;
  let boundedHeight = height;

  if (boundedWidth > maxDimension || boundedHeight > maxDimension) {
    const ratio = Math.min(maxDimension / boundedWidth, maxDimension / boundedHeight);
    boundedWidth = Math.max(1, Math.round(boundedWidth * ratio));
    boundedHeight = Math.max(1, Math.round(boundedHeight * ratio));
  }

  const pixelCount = boundedWidth * boundedHeight;
  if (pixelCount > maxPixels) {
    const ratio = Math.sqrt(maxPixels / pixelCount);
    boundedWidth = Math.max(1, Math.round(boundedWidth * ratio));
    boundedHeight = Math.max(1, Math.round(boundedHeight * ratio));
  }

  return { width: boundedWidth, height: boundedHeight };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Capture one bounded screen frame via getDisplayMedia.
 * Reused for viewport + all element crops to avoid repeated heavy captures.
 */
async function captureScreenFrame(): Promise<ScreenFrame> {
  const host = document.getElementById('bl-bug-reporter');
  if (host) host.style.display = 'none';
  await new Promise(r => setTimeout(r, 100));

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      video: { displaySurface: 'browser' } as any,
      preferCurrentTab: true,
    } as any);
  } catch (err) {
    if (host) host.style.display = '';
    throw err;
  }

  const video = document.createElement('video');
  try {
    video.srcObject = stream;
    await video.play();
    await new Promise(r => requestAnimationFrame(r));

    const sourceWidth = video.videoWidth;
    const sourceHeight = video.videoHeight;
    if (!sourceWidth || !sourceHeight) {
      throw new Error('Captured video frame has invalid dimensions.');
    }

    const bounded = getBoundedSize(sourceWidth, sourceHeight, MAX_CAPTURE_DIMENSION, MAX_CAPTURE_PIXELS);
    const canvas = document.createElement('canvas');
    canvas.width = bounded.width;
    canvas.height = bounded.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Unable to capture screenshot frame.');
    ctx.drawImage(video, 0, 0, bounded.width, bounded.height);

    return {
      canvas,
      scaleX: bounded.width / window.innerWidth,
      scaleY: bounded.height / window.innerHeight,
    };
  } finally {
    stream.getTracks().forEach(t => t.stop());
    try {
      video.pause();
      video.srcObject = null;
    } catch {
      // noop
    }
    if (host) host.style.display = '';
  }
}

function cropElementFromFrame(frame: ScreenFrame, element: HTMLElement): HTMLCanvasElement | null {
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;

  const sourceX = clamp(Math.floor(rect.left * frame.scaleX), 0, frame.canvas.width);
  const sourceY = clamp(Math.floor(rect.top * frame.scaleY), 0, frame.canvas.height);
  const sourceWidth = clamp(Math.ceil(rect.width * frame.scaleX), 1, frame.canvas.width - sourceX);
  const sourceHeight = clamp(Math.ceil(rect.height * frame.scaleY), 1, frame.canvas.height - sourceY);

  if (sourceWidth <= 0 || sourceHeight <= 0) return null;

  const bounded = getBoundedSize(sourceWidth, sourceHeight, MAX_CROP_DIMENSION, MAX_CROP_PIXELS);
  const cropCanvas = document.createElement('canvas');
  cropCanvas.width = bounded.width;
  cropCanvas.height = bounded.height;

  const ctx = cropCanvas.getContext('2d');
  if (!ctx) return null;

  ctx.drawImage(
    frame.canvas,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    bounded.width,
    bounded.height,
  );

  return cropCanvas;
}

/**
 * Capture a screenshot of the entire viewport.
 */
export async function captureViewportScreenshot(): Promise<string> {
  try {
    const frame = await captureScreenFrame();
    return compressScreenshot(frame.canvas.toDataURL('image/png'));
  } catch (err: any) {
    if (err?.name === 'NotAllowedError') {
      console.info('[BugReporter] Screenshot permission denied by user.');
    } else {
      console.warn('[BugReporter] Viewport screenshot failed:', err);
    }
    return '';
  }
}

/**
 * Capture a screenshot of a specific element.
 */
export async function captureElementScreenshot(element: HTMLElement): Promise<string> {
  try {
    const frame = await captureScreenFrame();
    const cropCanvas = cropElementFromFrame(frame, element);
    if (!cropCanvas) return '';

    const cropped = cropCanvas.toDataURL('image/png');
    return compressScreenshot(cropped, 500 * 1024);
  } catch (err: any) {
    if (err?.name === 'NotAllowedError') {
      console.info('[BugReporter] Screenshot permission denied by user.');
    } else {
      console.warn('[BugReporter] Element screenshot failed:', err);
    }
    return '';
  }
}

/**
 * Capture screenshots for all selected elements.
 * Uses a single captured frame to avoid repeated display-capture sessions.
 */
export async function captureAllScreenshots(
  selectedElements: SelectedElement[],
): Promise<{ viewport: string; elements: string[] }> {
  try {
    const frame = await captureScreenFrame();

    const viewportRaw = frame.canvas.toDataURL('image/png');
    const viewport = await compressScreenshot(viewportRaw);

    const elementScreenshots: string[] = [];
    for (const sel of selectedElements) {
      const cropCanvas = cropElementFromFrame(frame, sel.element);
      if (!cropCanvas) {
        elementScreenshots.push('');
        continue;
      }

      const cropped = cropCanvas.toDataURL('image/png');
      const compressed = await compressScreenshot(cropped, 500 * 1024);
      elementScreenshots.push(compressed);
    }

    return { viewport, elements: elementScreenshots };
  } catch (err: any) {
    if (err?.name === 'NotAllowedError') {
      console.info('[BugReporter] Screenshot permission denied by user.');
    } else {
      console.warn('[BugReporter] Screenshot capture failed:', err);
    }

    return {
      viewport: '',
      elements: selectedElements.map(() => ''),
    };
  }
}
